import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Package, ShoppingCart, Users, Building2, FileText, Command } from 'lucide-react';
import api from '../services/api';
import logger from '../utils/logger';

const SpotlightSearch = ({ open, onClose }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Sync with external open state
  useEffect(() => {
    if (open !== undefined) {
      setIsOpen(open);
    }
  }, [open]);

  // Listen for Ctrl+K / Cmd+K
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
      }
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    if (onClose) {
      onClose();
    }
  };

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Search with debounce
  useEffect(() => {
    if (!query.trim()) {
      setResults(null);
      return;
    }

    const debounce = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await api.get(`/search?q=${encodeURIComponent(query)}&limit=5`);
        setResults(response.data);
        setSelectedIndex(0);
      } catch (error) {
        logger.apiError('/search', error);
      } finally {
        setLoading(false);
      }
    }, 200);

    return () => clearTimeout(debounce);
  }, [query]);

  const getAllResults = () => {
    if (!results) return [];
    return [
      ...(results.products || []).map(p => ({ ...p, type: 'product', route: '/inventory/products' })),
      ...(results.orders || []).map(o => ({ ...o, type: 'order', route: '/orders' })),
      ...(results.suppliers || []).map(s => ({ ...s, type: 'supplier', route: '/suppliers' })),
      ...(results.warehouses || []).map(w => ({ ...w, type: 'warehouse', route: '/warehouses' }))
    ];
  };

  const handleNavigate = (item) => {
    navigate(item.route);
    handleClose();
    setQuery('');
  };

  const handleKeyDown = (e) => {
    const items = getAllResults();
    if (items.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev < items.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (items[selectedIndex]) {
          handleNavigate(items[selectedIndex]);
        }
        break;
    }
  };

  const getIcon = (type) => {
    const icons = {
      product: Package,
      order: ShoppingCart,
      supplier: Users,
      warehouse: Building2
    };
    return icons[type] || FileText;
  };

  if (!isOpen) return null;

  const allResults = getAllResults();

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="spotlight-search-label">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="flex min-h-screen items-start justify-center p-4 pt-[20vh]">
        <div className="relative w-full max-w-2xl bg-card rounded-xl shadow-2xl">
          {/* Search Input */}
          <div className="flex items-center gap-3 px-4 py-4 border-b border-border">
            <Search className="w-5 h-5 text-muted-foreground" aria-hidden="true" />
            <label htmlFor="spotlight-search" className="sr-only">Search products, orders, suppliers, and warehouses</label>
            <input
              id="spotlight-search"
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search products, orders, suppliers, warehouses..."
              className="flex-1 text-lg outline-none placeholder-muted-foreground bg-transparent text-foreground"
              role="searchbox"
              aria-autocomplete="list"
              aria-controls="spotlight-results"
              aria-expanded={allResults.length > 0}
              aria-activedescendant={allResults[selectedIndex] ? `result-${selectedIndex}` : undefined}
            />
            <div className="flex items-center gap-1 text-xs text-muted-foreground bg-muted px-2 py-1 rounded" aria-label="Keyboard shortcut: Control or Command + K">
              <Command size={12} aria-hidden="true" />
              <span>K</span>
            </div>
          </div>

          {/* Loading */}
          {loading && (
            <div className="px-4 py-8 text-center" role="status" aria-live="polite">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mx-auto" aria-hidden="true"></div>
              <span className="sr-only">Searching...</span>
            </div>
          )}

          {/* Results */}
          {!loading && query && allResults.length > 0 && (
            <div id="spotlight-results" className="max-h-96 overflow-y-auto" role="listbox" aria-label="Search results">
              {/* Products */}
              {results.products?.length > 0 && (
                <div className="px-2 py-2">
                  <div className="text-xs font-semibold text-gray-500 px-3 py-2">Products</div>
                  {results.products.map((item, idx) => {
                    const globalIdx = idx;
                    const Icon = getIcon('product');
                    return (
                      <button
                        key={item._id}
                        onClick={() => handleNavigate({ ...item, type: 'product', route: '/inventory/products' })}
                        className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left transition-colors ${
                          selectedIndex === globalIdx
                            ? 'bg-brand-600 text-white'
                            : 'hover:bg-gray-50 dark:hover:bg-dark-700'
                        }`}
                      >
                        <Icon className={`w-5 h-5 ${selectedIndex === globalIdx ? 'text-white' : 'text-brand-600'}`} />
                        <div className="flex-1">
                          <div className={`text-sm font-medium ${selectedIndex === globalIdx ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>
                            {item.name}
                          </div>
                          <div className={`text-xs ${selectedIndex === globalIdx ? 'text-brand-100' : 'text-gray-500 dark:text-gray-400'}`}>
                            ${item.price} • Stock: {item.stock}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Orders */}
              {results.orders?.length > 0 && (
                <div className="px-2 py-2 border-t border-gray-100 dark:border-dark-700">
                  <div className="text-xs font-semibold text-gray-500 px-3 py-2">Orders</div>
                  {results.orders.map((item, idx) => {
                    const globalIdx = (results.products?.length || 0) + idx;
                    const Icon = getIcon('order');
                    return (
                      <button
                        key={item._id}
                        onClick={() => handleNavigate({ ...item, type: 'order', route: '/orders' })}
                        className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left transition-colors ${
                          selectedIndex === globalIdx
                            ? 'bg-brand-600 text-white'
                            : 'hover:bg-gray-50 dark:hover:bg-dark-700'
                        }`}
                      >
                        <Icon className={`w-5 h-5 ${selectedIndex === globalIdx ? 'text-white' : 'text-brand-600'}`} />
                        <div className="flex-1">
                          <div className={`text-sm font-medium ${selectedIndex === globalIdx ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>
                            Order #{item._id?.slice(-6)} • {item.status}
                          </div>
                          <div className={`text-xs ${selectedIndex === globalIdx ? 'text-brand-100' : 'text-gray-500 dark:text-gray-400'}`}>
                            ${item.total}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Suppliers */}
              {results.suppliers?.length > 0 && (
                <div className="px-2 py-2 border-t border-gray-100 dark:border-dark-700">
                  <div className="text-xs font-semibold text-gray-500 px-3 py-2">Suppliers</div>
                  {results.suppliers.map((item, idx) => {
                    const globalIdx = (results.products?.length || 0) + (results.orders?.length || 0) + idx;
                    const Icon = getIcon('supplier');
                    return (
                      <button
                        key={item._id}
                        onClick={() => handleNavigate({ ...item, type: 'supplier', route: '/suppliers' })}
                        className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left transition-colors ${
                          selectedIndex === globalIdx
                            ? 'bg-brand-600 text-white'
                            : 'hover:bg-gray-50 dark:hover:bg-dark-700'
                        }`}
                      >
                        <Icon className={`w-5 h-5 ${selectedIndex === globalIdx ? 'text-white' : 'text-brand-600'}`} />
                        <div className="flex-1">
                          <div className={`text-sm font-medium ${selectedIndex === globalIdx ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>
                            {item.name}
                          </div>
                          <div className={`text-xs ${selectedIndex === globalIdx ? 'text-brand-100' : 'text-gray-500 dark:text-gray-400'}`}>
                            {item.email}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Warehouses */}
              {results.warehouses?.length > 0 && (
                <div className="px-2 py-2 border-t border-gray-100 dark:border-dark-700">
                  <div className="text-xs font-semibold text-gray-500 px-3 py-2">Warehouses</div>
                  {Array.isArray(results.warehouses) && results.warehouses.map((item, idx) => {
                    const globalIdx = (results.products?.length || 0) + (results.orders?.length || 0) + (results.suppliers?.length || 0) + idx;
                    const Icon = getIcon('warehouse');
                    return (
                      <button
                        key={item._id}
                        onClick={() => handleNavigate({ ...item, type: 'warehouse', route: '/warehouses' })}
                        className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left transition-colors ${
                          selectedIndex === globalIdx
                            ? 'bg-brand-600 text-white'
                            : 'hover:bg-gray-50 dark:hover:bg-dark-700'
                        }`}
                      >
                        <Icon className={`w-5 h-5 ${selectedIndex === globalIdx ? 'text-white' : 'text-brand-600'}`} />
                        <div className="flex-1">
                          <div className={`text-sm font-medium ${selectedIndex === globalIdx ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>
                            {item.name}
                          </div>
                          <div className={`text-xs ${selectedIndex === globalIdx ? 'text-brand-100' : 'text-gray-500 dark:text-gray-400'}`}>
                            {item.location?.city}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Footer */}
              <div className="px-4 py-3 border-t border-gray-100 dark:border-dark-700 text-xs text-gray-500 dark:text-gray-400 flex items-center justify-between bg-gray-50 dark:bg-dark-800">
                <div>
                  {results.total} results • {results.executionTime}ms
                </div>
                <div className="flex items-center gap-3">
                  <span>↑↓ Navigate</span>
                  <span>↵ Select</span>
                  <span>ESC Close</span>
                </div>
              </div>
            </div>
          )}

          {/* No Results */}
          {!loading && query && allResults.length === 0 && (
            <div className="px-4 py-8 text-center text-gray-500">
              No results found for &quot;{query}&quot;
            </div>
          )}

          {/* Empty State */}
          {!query && (
            <div className="px-4 py-8 text-center text-gray-400">
              <p className="text-sm">Start typing to search everything...</p>
              <p className="text-xs mt-2">Products • Orders • Suppliers • Warehouses</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SpotlightSearch;
